import React, { useState } from 'react';
import { Button } from '../../ui/button';

const BITS_MSB_FIRST = [7, 6, 5, 4, 3, 2, 1, 0];
const COLLAPSE_ABOVE_BYTES = 16;
const MAX_SHOWN_BYTES = 72; // a bad Motorola signal near the end of a 64-byte frame can run a little past it

const hex2 = (b) => b.toString(16).toUpperCase().padStart(2, '0');

// Bytes are drawn as wrapping blocks (Byte 0 | Byte 1 | ...), bits 7..0 left to right,
// so it reads the same on a phone as on a desktop. Positions come from the backend's
// layout, so the picture always matches the decode and the DBC.
const BitVisualization = ({ payloadLength, dataBytes, layout, siblings = [], testId }) => {
  const [showAll, setShowAll] = useState(false);

  const own = new Map((layout?.bits || []).map((b) => [`${b.byte}-${b.bit}`, b]));
  const other = new Map();
  siblings.forEach((s) => s.bits.forEach((b) => {
    const key = `${b.byte}-${b.bit}`;
    other.set(key, [...(other.get(key) || []), s.name]);
  }));

  const payload = payloadLength ?? (dataBytes ? dataBytes.length : 8);
  const signalLast = layout ? Math.max(...layout.bits.map((b) => b.byte)) : -1;
  const total = Math.min(Math.max(payload, signalLast + 1), MAX_SHOWN_BYTES);
  const outside = layout ? layout.bits.filter((b) => b.byte >= payload).length : 0;

  const usedBytes = new Set([...(layout?.bytes || []), ...siblings.flatMap((s) => s.bits.map((b) => b.byte))]);
  const collapsible = total > COLLAPSE_ABOVE_BYTES;
  const byteIndices = Array.from({ length: total }, (_, i) => i)
    .filter((i) => !collapsible || showAll || usedBytes.has(i));

  const cell = (byte, bit) => {
    const key = `${byte}-${bit}`;
    const mine = own.get(key);
    const others = other.get(key);
    const beyond = byte >= payload;
    const value = dataBytes && byte < dataBytes.length ? (dataBytes[byte] >> bit) & 1 : null;
    const isMsb = mine && layout.msb.byte === byte && layout.msb.bit === bit;
    const isLsb = mine && layout.lsb.byte === byte && layout.lsb.bit === bit;

    let cls = 'bg-background text-muted-foreground border-structure';
    let style;
    if (mine && others) {
      cls = 'text-white border-red-700';
      style = { background: 'repeating-linear-gradient(45deg,#dc2626,#dc2626 4px,#7f1d1d 4px,#7f1d1d 8px)' };
    } else if (mine && beyond) {
      cls = 'bg-red-100 text-red-700 border-red-500 border-dashed dark:bg-red-950/40 dark:text-red-300';
    } else if (mine) {
      cls = 'bg-autoxium-blue text-white border-autoxium-blue';
    } else if (others) {
      cls = 'bg-amber-200 text-amber-900 border-amber-400 dark:bg-amber-900/50 dark:text-amber-100';
    }

    const title = [
      `Byte ${byte}, bit ${bit} (DBC bit ${byte * 8 + bit})`,
      mine ? `Signal bit ${mine.significance}${isMsb ? ' (MSB)' : isLsb ? ' (LSB)' : ''}` : null,
      others ? `Also used by: ${others.join(', ')}` : null,
      beyond ? 'Outside the payload' : null,
      value === null ? null : `Value ${value}`,
    ].filter(Boolean).join(' · ');

    return (
      <div key={bit} className="flex flex-col items-center gap-0.5">
        <div
          title={title}
          style={style}
          className={`relative h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center rounded border text-xs font-mono font-bold ${cls}`}
          data-testid={`${testId}-cell-${byte}-${bit}`}
          data-signal={mine ? 'true' : undefined}
        >
          {value === null ? '·' : value}
          {(isMsb || isLsb) && (
            <span className="absolute -top-2 -right-1.5 rounded-sm bg-foreground text-background px-0.5 text-[9px] leading-tight">
              {isMsb && isLsb ? 'M/L' : isMsb ? 'MSB' : 'LSB'}
            </span>
          )}
        </div>
        <span className="text-[9px] font-mono text-muted-foreground/70">{byte * 8 + bit}</span>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap gap-3">
        {byteIndices.map((byte) => {
          const beyond = byte >= payload;
          const value = dataBytes && byte < dataBytes.length ? dataBytes[byte] : null;
          return (
            <div
              key={byte}
              className={`rounded-md border p-2 ${beyond ? 'border-dashed border-red-400 bg-red-50/50 dark:bg-red-950/20' : 'border-structure bg-muted/30'}`}
              data-testid={`${testId}-byte-${byte}`}
            >
              <div className="flex items-center justify-between mb-2 font-mono text-xs">
                <span className="font-bold">Byte {byte}</span>
                <span className={beyond ? 'text-red-600' : 'text-muted-foreground'}>
                  {beyond ? 'outside payload' : value === null ? '--' : `0x${hex2(value)}`}
                </span>
              </div>
              <div className="flex gap-0.5 sm:gap-1 pt-1">
                {BITS_MSB_FIRST.map((bit) => cell(byte, bit))}
              </div>
            </div>
          );
        })}
      </div>

      {collapsible && (
        <Button type="button" variant="outline" size="sm" onClick={() => setShowAll((v) => !v)} data-testid={`${testId}-toggle`}>
          {showAll ? 'Show only bytes with signal bits' : `Show all ${total} bytes`}
        </Button>
      )}

      {outside > 0 && (
        <p className="text-xs text-destructive" data-testid={`${testId}-outside`}>
          {outside} signal bit{outside === 1 ? '' : 's'} fall outside the {payload}-byte payload.
        </p>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-autoxium-blue" /> This signal</span>
        {siblings.length > 0 && (
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-amber-300" /> Other signals in this message</span>
        )}
        {siblings.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: 'repeating-linear-gradient(45deg,#dc2626,#dc2626 2px,#7f1d1d 2px,#7f1d1d 4px)' }} /> Overlap
          </span>
        )}
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-structure bg-background" /> Unused</span>
        <span>Small numbers = DBC bit number (byte × 8 + bit)</span>
      </div>
    </div>
  );
};

export default BitVisualization;
